import { Card, CardContent, CardHeader, Divider, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useActivityReport } from '../../sic-lib/api/hooks/useActivityReport';
import { GenericReducer, genericReducer } from '../../sic-lib/reducers/genericReducer';
import { useAppSelector } from '../../store/hooks';
import Loader from '../Shared/Loader';
import AllContactActivity from './AllContactActivity';
import NewContactsReport from './NewContactsReport';
import SelectUserReport from './SelectUserReport';
import UsageReport from './UsageActivityReport';

export default function ActivityReport() {
    const { classes } = useStyles();

    const user = useAppSelector(state => state.user.userName);
    const dt = DateTime.now();

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        username: user,
        startDate: dt.set({ year: dt.year, month: dt.month, day: dt.day - 1, hour: 0, minute: 0 }),
        endDate: dt.set({ year: dt.year, month: dt.month, day: dt.day, hour: 23, minute: 59 }),
        users: [],
    });

    const { data: reportData, isLoading: isReportLoading } = useActivityReport({
        username: formState.username,
        startDate: formState.startDate,
        endDate: formState.endDate,
        users: formState.users,
    });

    const twilioStats = React.useMemo(() => {
        return reportData?.twilioStats || [];
    }, [reportData?.twilioStats]);

    const handleStartDateUpdate = (date: DateTime | null) => {
        if (!date) return;
        formDispatch({
            ...formState,
            startDate: date,
        });
    };

    const handleEndDateUpdate = (date: DateTime | null) => {
        if (!date) return;
        formDispatch({
            ...formState,
            endDate: date,
        });
    };

    return (
        <>
            <Card>
                <CardHeader title="Time Period" />
                <Divider />
                <CardContent>
                    <Stack
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="center"
                        // spacing={2}
                    >
                        <DateTimePicker
                            label="Start Date"
                            defaultValue={formState.startDate}
                            className={classes.dateField}
                            onChange={handleStartDateUpdate}
                        />
                        <DateTimePicker
                            label="End Date"
                            defaultValue={formState.endDate}
                            className={classes.dateField}
                            onChange={handleEndDateUpdate}
                        />
                        <SelectUserReport
                            fullWidth
                            required
                            label="Select User"
                            usernames={formState.users}
                            onSelectHandler={formDispatch}
                        />
                        <Stack ml={isReportLoading ? 2 : 0}>
                            <Loader isLoading={isReportLoading}></Loader>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
            <NewContactsReport newContacts={reportData?.newLeads} />
            <AllContactActivity report={reportData} />
            <UsageReport twilioStats={twilioStats} />
        </>
    );
}

interface FormState {
    username: string;
    startDate: DateTime;
    endDate: DateTime;
    users: string[];
}

const useStyles = makeStyles()((theme: Theme) => ({
    dateField: {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        width: 380,
    },
}));
